// src/pages/CompanyPolicy.tsx
import React, { useEffect, useMemo, useState } from 'react';
import {
  BookOpen, Plus, Search, Pencil, Trash2, ChevronLeft,
  Eye, Clock, FileText, ShieldCheck,
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { useAccess } from '../contexts/AccessContext';
import companyPolicyApi from '../api/companyPolicyApi';
import { CompanyPolicy as Policy } from '../types/companyPolicy';
import { CompanyPolicyForm } from '../components/CompanyPolicyForm';
import { PolicyMarkdown } from '../components/PolicyMarkdown';
import { usePermissionGuard } from '../hooks/usePermissionGuard';
import { Popup } from '../components/Popup';
import ConfirmDialog, { ConfirmState } from '../components/ConfirmDialog';

const fmtDate = (s?: string) => {
  if (!s) return '';
  const d = new Date(s);
  if (isNaN(d.getTime())) return '';
  return d.toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' });
};

export function CompanyPolicy() {
  const { user } = useAuth();
  const { hasAccess, canManage: canManageAccess } = useAccess();
  const isAdmin = (user?.role || '').trim().toLowerCase() === 'admin';
  // Everyone can read. Editing/adding is for admins plus anyone explicitly
  // granted the Company Policy panel at "manage" level in their Access profile.
  // A user with no Access profile (permissive) is NOT given write here — the
  // page is public-view, so write must be an explicit grant.
  const canManage = isAdmin || (hasAccess && canManageAccess('set-company-policy'));

  const [policies, setPolicies] = useState<Policy[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [mobileReading, setMobileReading] = useState(false);
  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState<Policy | null>(null);
  const [saving, setSaving] = useState(false);
  const [confirm, setConfirm] = useState<ConfirmState | null>(null);
  const { showError, showSuccess, popup, closePopup } = usePermissionGuard();

  const load = async () => {
    setLoading(true);
    try {
      const res = await companyPolicyApi.getPolicies();
      const data: Policy[] = Array.isArray(res) ? res : res?.data || [];
      setPolicies(data);
      setSelectedId((prev) => (prev && data.some((p) => p.id === prev) ? prev : data[0]?.id || null));
    } catch (err: any) {
      showError('Failed to load', err?.response?.data?.detail || err?.message || 'Could not load policies');
      setPolicies([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return policies;
    return policies.filter((p) =>
      p.title?.toLowerCase().includes(q) ||
      p.category?.toLowerCase().includes(q) ||
      p.content?.toLowerCase().includes(q)
    );
  }, [policies, search]);

  // Group filtered policies by category, preserving first-seen order.
  const groups = useMemo(() => {
    const map = new Map<string, Policy[]>();
    for (const p of filtered) {
      const c = p.category || 'General';
      if (!map.has(c)) map.set(c, []);
      map.get(c)!.push(p);
    }
    return Array.from(map.entries());
  }, [filtered]);

  const selected = useMemo(
    () => filtered.find((p) => p.id === selectedId) || filtered[0] || null,
    [filtered, selectedId]
  );

  const openCreate = () => { setEditing(null); setFormOpen(true); };
  const openEdit = (p: Policy) => { setEditing(p); setFormOpen(true); };

  const handleSubmit = async (data: Partial<Policy>) => {
    setSaving(true);
    try {
      if (editing?.id) {
        await companyPolicyApi.updatePolicy(editing.id, data);
        showSuccess('Saved', 'Policy updated successfully');
      } else {
        const created = await companyPolicyApi.createPolicy(data as any);
        if (created?.id) setSelectedId(created.id);
        showSuccess('Created', 'Policy published successfully');
      }
      setFormOpen(false);
      setEditing(null);
      await load();
    } catch (err: any) {
      showError('Save failed', err?.response?.data?.detail || err?.message || 'Could not save the policy');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (p: Policy) => {
    if (!p.id) return;
    setConfirm({
      title: `Delete "${p.title}"?`,
      description: 'This cannot be undone.',
      confirmLabel: 'Delete',
      tone: 'danger',
      onConfirm: async () => {
        try {
          await companyPolicyApi.deletePolicy(p.id);
          showSuccess('Deleted', 'Policy removed');
          if (selectedId === p.id) setSelectedId(null);
          await load();
        } catch (err: any) {
          showError('Delete failed', err?.response?.data?.detail || err?.message || 'Could not delete the policy');
        }
      },
    });
  };

  return (
    <div className="min-h-full bg-[#faf8f3]">
      <div className="max-w-6xl mx-auto p-4 sm:p-6">
        {/* Page header */}
        <div className="flex items-center justify-between gap-4 mb-5">
          <div className="flex items-center gap-3">
            <span className="w-11 h-11 rounded-xl bg-[#e8efe9] text-[#0f2d1f] flex items-center justify-center">
              <BookOpen className="w-[22px] h-[22px]" />
            </span>
            <div>
              <h1 className="text-[22px] font-semibold text-[#1a1a1a] leading-tight tracking-[-0.01em]">Company policy</h1>
              <p className="text-[13px] text-[#8a8580] mt-0.5">
                {canManage ? 'Manage the policies your team sees' : 'Official policies & guidelines for the team'}
              </p>
            </div>
          </div>
          {canManage ? (
            <button onClick={openCreate}
              className="shrink-0 flex items-center gap-2 px-4 py-2.5 text-[13.5px] font-medium text-white bg-[#0f2d1f] rounded-xl hover:bg-[#0a1f15] active:scale-[0.98] transition shadow-sm">
              <Plus className="w-4 h-4" /> <span className="hidden sm:inline">Add policy</span><span className="sm:hidden">Add</span>
            </button>
          ) : (
            <span className="shrink-0 flex items-center gap-1.5 px-3 py-2 text-[12px] font-medium text-[#1f4030] bg-[#e8efe9] rounded-full">
              <Eye className="w-3.5 h-3.5" /> Read only
            </span>
          )}
        </div>

        {/* Card */}
        <div className="bg-white border border-[#e8e3d8] rounded-2xl overflow-hidden shadow-[0_1px_2px_rgba(15,45,31,0.04)]">
          {loading ? (
            <div className="flex flex-col items-center justify-center py-24 gap-3">
              <div className="w-8 h-8 rounded-full border-2 border-[#e8efe9] border-t-[#0f2d1f] animate-spin" />
              <p className="text-[13px] text-[#8a8580]">Loading policies…</p>
            </div>
          ) : policies.length === 0 ? (
            <EmptyState canManage={canManage} onAdd={openCreate} />
          ) : (
            <div className="grid md:grid-cols-[290px_1fr] min-h-[460px]">
              {/* Index */}
              <aside className={`${mobileReading ? 'hidden' : 'block'} md:block border-b md:border-b-0 md:border-r border-[#e8e3d8] bg-[#fbf8f1]`}>
                <div className="p-3">
                  <div className="relative mb-2">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#a39e95]" />
                    <input
                      value={search}
                      onChange={(e) => setSearch(e.target.value)}
                      placeholder="Search policies"
                      className="w-full pl-9 pr-3 py-2.5 text-[13px] bg-white border border-[#e8e3d8] rounded-lg outline-none focus:border-[#0f2d1f] focus:ring-2 focus:ring-[#0f2d1f]/10 transition"
                    />
                  </div>

                  <div className="max-h-[60vh] md:max-h-[420px] overflow-y-auto -mx-1 px-1">
                    {groups.length === 0 ? (
                      <p className="text-[13px] text-[#8a8580] px-2 py-6 text-center">No policies match “{search}”.</p>
                    ) : groups.map(([cat, items]) => (
                      <div key={cat} className="mb-3 last:mb-1">
                        <p className="px-2 pt-2 pb-1.5 text-[10.5px] font-semibold tracking-[0.05em] uppercase text-[#a39e95]">{cat}</p>
                        {items.map((p) => {
                          const active = selected?.id === p.id;
                          return (
                            <button key={p.id}
                              onClick={() => { setSelectedId(p.id!); setMobileReading(true); }}
                              className={`group w-full flex items-center gap-2.5 px-2.5 py-2 mb-0.5 rounded-lg text-left transition ${active ? 'bg-[#0f2d1f] text-white' : 'text-[#4a4a4a] hover:bg-[#eef2ec]'}`}>
                              <FileText className={`w-[15px] h-[15px] shrink-0 ${active ? 'text-white/80' : 'text-[#a39e95]'}`} />
                              <span className={`flex-1 text-[13px] truncate ${active ? 'font-medium' : ''}`}>{p.title}</span>
                              {p.is_active === false && (
                                <span className={`text-[10px] px-1.5 py-0.5 rounded ${active ? 'bg-white/20 text-white' : 'bg-[#fef3c7] text-[#946c1a]'}`}>Draft</span>
                              )}
                            </button>
                          );
                        })}
                      </div>
                    ))}
                  </div>
                </div>
              </aside>

              {/* Reader */}
              <section className={`${mobileReading ? 'block' : 'hidden'} md:block`}>
                {selected ? (
                  <article className="p-5 sm:p-8 max-w-2xl">
                    <button onClick={() => setMobileReading(false)}
                      className="md:hidden inline-flex items-center gap-1 mb-4 text-[13px] text-[#1f4030] font-medium">
                      <ChevronLeft className="w-4 h-4" /> All policies
                    </button>

                    <div className="flex items-start justify-between gap-4">
                      <div>
                        <h2 className="text-[24px] font-semibold text-[#1a1a1a] leading-tight tracking-[-0.015em]">{selected.title}</h2>
                        <div className="flex flex-wrap items-center gap-x-3 gap-y-1.5 mt-2.5">
                          <span className="text-[11.5px] font-medium text-[#1f4030] bg-[#e8efe9] px-2.5 py-1 rounded-full">{selected.category || 'General'}</span>
                          {selected.is_active === false && (
                            <span className="text-[11.5px] font-medium text-[#946c1a] bg-[#fef3c7] px-2.5 py-1 rounded-full">Draft — not visible to staff</span>
                          )}
                          {(selected.updated_at || selected.created_at) && (
                            <span className="inline-flex items-center gap-1 text-[12px] text-[#8a8580]">
                              <Clock className="w-3.5 h-3.5" /> Updated {fmtDate(selected.updated_at || selected.created_at)}
                            </span>
                          )}
                        </div>
                      </div>
                      {canManage && (
                        <div className="flex items-center gap-1 shrink-0">
                          <button onClick={() => openEdit(selected)} title="Edit"
                            className="p-2 text-[#1f4030] hover:bg-[#e8efe9] rounded-lg transition"><Pencil className="w-4 h-4" /></button>
                          <button onClick={() => handleDelete(selected)} title="Delete"
                            className="p-2 text-[#b03b3b] hover:bg-[#fdecea] rounded-lg transition"><Trash2 className="w-4 h-4" /></button>
                        </div>
                      )}
                    </div>

                    <div className="mt-6 pt-6 border-t border-[#f0ece3]">
                      <PolicyMarkdown content={selected.content} />
                    </div>
                  </article>
                ) : (
                  <div className="flex items-center justify-center h-full py-24 text-[#b8b3ac] text-[13px]">Select a policy to read.</div>
                )}
              </section>
            </div>
          )}
        </div>

        {/* Footer reassurance for staff */}
        {!canManage && policies.length > 0 && (
          <p className="flex items-center justify-center gap-1.5 text-[12px] text-[#a39e95] mt-4">
            <ShieldCheck className="w-3.5 h-3.5" /> Maintained by your administrator
          </p>
        )}
      </div>

      {canManage && (
        <CompanyPolicyForm
          isOpen={formOpen}
          onClose={() => { setFormOpen(false); setEditing(null); }}
          onSubmit={handleSubmit}
          initialData={editing}
          isLoading={saving}
        />
      )}

      <Popup isOpen={popup.isOpen} type={popup.type} title={popup.title} message={popup.message} onClose={closePopup} />

      <ConfirmDialog state={confirm} onClose={() => setConfirm(null)} />
    </div>
  );
}

function EmptyState({ canManage, onAdd }: { canManage: boolean; onAdd: () => void }) {
  return (
    <div className="flex flex-col items-center justify-center text-center py-24 px-6">
      <span className="w-16 h-16 rounded-2xl bg-[#e8efe9] text-[#0f2d1f] flex items-center justify-center mb-4">
        <BookOpen className="w-8 h-8" />
      </span>
      <h3 className="text-[17px] font-semibold text-[#1a1a1a]">
        {canManage ? 'No policies yet' : 'No policies published yet'}
      </h3>
      <p className="text-[13.5px] text-[#8a8580] mt-1.5 max-w-sm">
        {canManage
          ? 'Create your first company policy. Once published, every team member will be able to read it here.'
          : 'Your administrator hasn’t published any company policies yet. Please check back soon.'}
      </p>
      {canManage && (
        <button onClick={onAdd}
          className="mt-5 flex items-center gap-2 px-4 py-2.5 text-[13.5px] font-medium text-white bg-[#0f2d1f] rounded-xl hover:bg-[#0a1f15] active:scale-[0.98] transition">
          <Plus className="w-4 h-4" /> Add your first policy
        </button>
      )}
    </div>
  );
}

export default CompanyPolicy;
